<template>
  <section class="mb-8">
    <h3
      id="required-manuscript"
      class="font-roboto text-xl font-bold mb-2">
      Manuscript file
    </h3>
    <div id="pnlManuscriptNotLoaded">
      <p class="mb-4">
        Word, PDF or LaTex file
      </p>

      <FormInputFile
        ref="fileInput"
        id="mainArticle"
        :multiple="false"
        :accept="[
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          '.doc',
          '.docx',
          'application/pdf',
          '.pdf',
          'application/x-latex',
          'text/plain',
          '.tex'
        ]"
        :disabled="mainArticle"
        @custom-change="fileChanged"
        buttonDataTestId="upload-manuscript-file"
        :inline="true"
        :showSelectedFiles="false"
        class="mb-4"
        v-if="!!!mainArticle"
      >
      </FormInputFile>

      <UploadedFileList :fileListType="SubmissionFileType.MainArticle" />
      <NotificationComponent
        v-if="hasError"
        data-testid="failed-to-upload-manuscript"
        class="mb-8"
        role="alert"
        variant="error"
        heading-text="Upload error"
        :heading-level="1">
        <template #body>
          <UploadErrorMessage
            fileTypeLabel="Manuscript"
            dataTestId="manuscript"
            testId="Manuscript"
          />
        </template>
      </NotificationComponent>
    </div>
    <InlineWarningMessage
      v-if="error ?? localError"
      :errorMessage="error ?? localError"
      errorTestId="manuscript-upload"
    />
    <div id="divAuthorContributionsInfo">
      <AuthorContributionsInfo/>
    </div>
  </section>
</template>

<script setup lang="ts">
import { SubmissionFileType, SubmissionFile } from '@/types'
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useFileUpload } from '@/composables/useFileUpload'
import AuthorContributionsInfo from '../upload/AuthorContributionsInfo.vue'
import InlineWarningMessage from '../common/InlineWarningMessage.vue'
import UploadedFileList from '../upload/UploadedFileList.vue'
import FormInputFile from '../common/FormInputFile.vue'
import NotificationComponent from '../ui-components/NotificationComponent.vue'
import UploadErrorMessage from './UploadErrorMessage.vue'

const store = useStore()
const fileInput = ref<HTMLInputElement | null>(null)

const { uploadFile, localErrors, clearErrors } = useFileUpload()

defineProps<{ error?: string }>()
const emit = defineEmits(['clear-error'])

const mainArticle = computed((): SubmissionFile => {
  return store.getters.submissionFiles.find((file: { type: SubmissionFileType }) => file.type === SubmissionFileType.MainArticle) as SubmissionFile
})

const hasError = ref(false)

const localError = computed(() => {
  if (localErrors.value.length > 0) {
    return localErrors.value[0].text
  }
  return undefined
})

const fileChanged = async (fileArray: FileList | File[] | null = null) => {
  if (!fileArray && !fileInput.value) return
  const inputFiles = fileArray as FileList | File[] | null
  if (!inputFiles || !inputFiles[0]) {
    return
  }

  clearErrors()
  hasError.value = false
  const file = inputFiles[0] as File

  await uploadFile(
    file,
    SubmissionFileType.MainArticle,
    'mainarticle',
    {
      successMessage: 'Your manuscript has been successfully added.',
      onSuccess: () => {
        emit('clear-error')
        hasError.value = false
      },
      onError: () => {
        hasError.value = true
      },
      onValidationError: (error, file) => {
        emit('clear-error')
        localErrors.value.push({
          fileName: file.name,
          text: error instanceof Error ? error.message : 'Validation error',
        })
      },
    },
  )
}
</script>
